import { useMemo } from 'react'

import type {
  Mission,
  MissionProgress
} from '@/features/missions/types/mission'
import { useUserProgress } from '@/shared/contexts/UserProgress'
import { useCurrentUser } from '@/shared/hooks/useCurrentUser'
import { useGamificationData } from '@/shared/hooks/useGamificationData'

interface UseMissionsStoreProps {
  id?: number
  uid?: string
}

export const useMissionsStore = ({ id, uid }: UseMissionsStoreProps = {}) => {
  const data = useGamificationData()
  const missionsRaw = data?.missions ?? []
  const { currentUser } = useCurrentUser()

  const { progress, missionProgresses, refetchMissionProgresses } =
    useUserProgress()

  const getProgress = (mission: Mission): MissionProgress => {
    if (mission.kind === 'evidence') {
      const submissions = (progress?.mission_submissions ?? []).filter(
        submission => submission.mission_id === mission.id
      )

      const completed = submissions.some(
        submission => submission.status === 'approved'
      )

      return {
        mission_id: mission.id,
        completed_at: null,
        completed,
        claimed: completed,
        claimed_at: completed ? new Date().toISOString() : null,
        period_key: null,
        total_count: 1,
        current_count: completed ? 1 : 0,
        percentage: completed ? 100 : 0
      }
    }

    const missionProgress = missionProgresses.find(
      mp => mp.mission_id === mission.id
    )

    if (!missionProgress)
      return {
        mission_id: mission.id,
        completed_at: null,
        completed: false,
        claimed: false,
        claimed_at: null,
        period_key: null,
        total_count: 1,
        current_count: 0,
        percentage: 0
      }

    const total_count = mission.objective_size || 1
    const current_count = Math.min(
      (missionProgress?.current_count as number) || 0,
      total_count
    )
    const percentage = Math.round((current_count / total_count) * 100)

    return {
      ...missionProgress,
      completed: current_count >= total_count,
      claimed: !!missionProgress?.completed_at,
      claimed_at: missionProgress?.completed_at as string | null,
      period_key: missionProgress?.period_key as string | null,
      total_count,
      current_count,
      percentage
    }
  }

  const missions = useMemo(() => {
    // Duas fontes: o payload global (/gamification/data), que traz as missões
    // SEM regra de desbloqueio, e o payload por usuário (/gamification/progress),
    // que traz as COM regra já avaliadas para este aluno. A dedupe por uid é
    // defensiva — as duas listas são disjuntas por construção no backend, mas
    // uma regressão lá não pode virar card duplicado aqui.
    const gatedMissions = progress?.missions ?? []
    const seenUids = new Set(gatedMissions.map(mission => mission.uid))
    const allMissions = [
      ...gatedMissions,
      ...missionsRaw.filter(mission => !seenUids.has(mission.uid))
    ]

    const filteredMissions = allMissions.filter(mission => {
      if (mission.school_ids?.length > 0) {
        return mission.school_ids.includes(currentUser?.school_id)
      }
      return true
    })

    const sortedMissions = filteredMissions.sort((a, b) => {
      return a.created_at.localeCompare(b.created_at)
    })

    return sortedMissions.map(mission => {
      const _progress = getProgress(mission)

      const isGeneral =
        mission.kind !== 'progress' || mission.recurrence_kind === 'lifetime'

      let imageUrl = mission?.icon
        ? `/icons/${mission.icon}.svg`
        : mission?.image?.file?.url || '/icons/default-mission.svg'

      if (isGeneral && mission.image?.file?.url) {
        imageUrl = mission.image?.file?.url
      }

      return {
        ...mission,
        imageUrl,
        _progress,
        isGeneral
      }
    })
  }, [missionsRaw, missionProgresses, progress])

  const mission = useMemo(() => {
    if (!id && !uid) return null
    return missions.find(mission => mission.id === id || mission.uid === uid)
  }, [missions, id, uid])

  return { missions, mission, refetchMissionProgresses }
}
